import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTestimonials } from "@/hooks/useData";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminHeader, DeleteButton } from "@/components/admin/AdminUI";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { StarRating } from "@/components/StarRating";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil } from "lucide-react";

const EMPTY = { author_name: "", author_title: "", location: "", project_type: "", rating: 5, quote: "", avatar_url: "", featured: true, order: 0 };

export default function ManageTestimonials() {
  const { data: items = [] } = useTestimonials();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...EMPTY, ...it }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form, rating: Number(form.rating), order: Number(form.order) };
      if (editing) await api.put(`/admin/testimonials/${editing.id}`, payload);
      else await api.post("/admin/testimonials", payload);
      qc.invalidateQueries({ queryKey: ["testimonials"] });
      toast.success(editing ? "Testimonial updated" : "Testimonial added");
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/testimonials/${id}`);
    qc.invalidateQueries({ queryKey: ["testimonials"] });
    toast.success("Deleted");
  };

  return (
    <div data-testid="manage-testimonials">
      <AdminHeader eyebrow="Client Feedback" title="Testimonials" onAdd={openNew} addLabel="Add Testimonial" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-[#ECEAE4] p-6" data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex justify-between items-start">
              <StarRating rating={t.rating} />
              <div className="flex items-center gap-1">
                {t.featured && <span className="label-eyebrow text-[9px] text-[#9E8773] mr-1">Featured</span>}
                <button onClick={() => openEdit(t)} className="text-[#5E5A54] hover:text-[#2C2A29] p-2" data-testid={`edit-testimonial-${t.id}`}><Pencil size={15} /></button>
                <DeleteButton onConfirm={() => remove(t.id)} label="testimonial" testid={`delete-testimonial-${t.id}`} />
              </div>
            </div>
            <p className="text-[#2C2A29] font-light mt-3 line-clamp-3">“{t.quote}”</p>
            <p className="text-sm text-[#5E5A54] mt-3 font-medium">{t.author_name} <span className="font-light">· {t.author_title}</span></p>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#FFFFFF] rounded-none border-[#ECEAE4] max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-3xl">{editing ? "Edit" : "New"} Testimonial</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Field label="Author name" required value={form.author_name} onChange={(v) => set("author_name", v)} testid="t-author" />
              <Field label="Title / role" value={form.author_title} onChange={(v) => set("author_title", v)} testid="t-title" />
              <Field label="Location" value={form.location} onChange={(v) => set("location", v)} testid="t-location" />
              <Field label="Project type" value={form.project_type} onChange={(v) => set("project_type", v)} testid="t-project" />
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow text-[#5E5A54]">Quote</Label>
              <Textarea required rows={4} value={form.quote} onChange={(e) => set("quote", e.target.value)} className="rounded-none bg-white border-[#ECEAE4]" data-testid="t-quote" />
            </div>
            <div className="grid grid-cols-2 gap-4 items-end">
              <div className="space-y-1.5">
                <Label className="label-eyebrow text-[#5E5A54]">Rating</Label>
                <Select value={String(form.rating)} onValueChange={(v) => set("rating", v)}>
                  <SelectTrigger className="rounded-none bg-white border-[#ECEAE4]" data-testid="t-rating"><SelectValue /></SelectTrigger>
                  <SelectContent>{[5, 4, 3, 2, 1].map((r) => <SelectItem key={r} value={String(r)}>{r} Stars</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Field label="Display order" type="number" value={form.order} onChange={(v) => set("order", v)} testid="t-order" />
            </div>
            <div className="flex items-center justify-between border border-[#ECEAE4] bg-white px-4 py-3">
              <Label className="label-eyebrow text-[#5E5A54]">Feature on homepage</Label>
              <Switch checked={form.featured} onCheckedChange={(v) => set("featured", v)} data-testid="t-featured" />
            </div>
            <Button type="submit" disabled={saving} className="w-full rounded-none bg-[#2C2A29] text-[#FFFFFF] h-12 label-eyebrow text-[10px]" data-testid="t-save">
              {saving ? "Saving…" : "Save Testimonial"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testid }) => (
  <div className="space-y-1.5">
    <Label className="label-eyebrow text-[#5E5A54]">{label}</Label>
    <Input type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="rounded-none bg-white border-[#ECEAE4]" data-testid={testid} />
  </div>
);
